import type { DrugCard } from '@/entities/drug/api/drug.api';
import { DrugBadges } from '@/entities/drug/components/DrugBadges';
import {
  displayDrugCode,
  formatFeeRate,
} from '@/entities/drug/utils/drug.utils';
import { DrugNameLink } from '@/shared/components/drug-drawer/DrugNameLink';
import { cn } from '@/shared/lib/utils';
import { formatWon, formatYm } from '@/shared/utils/format';
import { Fragment } from 'react';

/**
 * M-11 결과 카드 — 카드 전체나 [상세 보기]를 누르면 상세 드로어(M-12)를 연다.
 * 수수료율이 없으면(거래 불가 · 미계약) "-%"를 회색으로 낮추고 목록에서는 빼지 않는다.
 */
export const DrugResultCard = ({
  drug,
  active,
  onOpen,
}: {
  drug: DrugCard;
  /** 지금 드로어에 열려 있는 약 */
  active: boolean;
  onOpen: () => void;
}) => {
  const meta = [
    displayDrugCode(drug.drugCode),
    drug.mainIngredientName,
    drug.pharmaCompanyName,
    drug.reimbursementType,
  ].filter((v): v is string => !!v);
  const rates = [
    { label: '제약사 수수료율', value: drug.pharmaFeeRate },
    { label: '사용자 수수료율', value: drug.userFeeRate },
  ];

  return (
    <article
      onClick={onOpen}
      // 카드에 포커스가 잡혀야 드로어를 닫았을 때 이 카드로 포커스가 돌아온다 (M-15 D)
      tabIndex={0}
      aria-label={`${drug.drugName} 상세 보기`}
      onKeyDown={(e) => {
        if (
          e.target === e.currentTarget &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        'flex cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/40 flex-col rounded-card border bg-surface px-5 py-[18px] transition-colors',
        active
          ? 'border-primary shadow-[0_0_0_0.5px_var(--color-primary)]'
          : 'border-line hover:border-primary',
      )}
    >
      <DrugBadges
        isReferenceDrug={drug.referenceDrug}
        isBioequivalent={drug.bioequivalent}
        className="mb-2.5"
      />
      <h3 className="text-sm leading-[21px] font-medium text-ink">
        <DrugNameLink drugCode={drug.drugCode} preview={drug}>
          {drug.drugName}
        </DrugNameLink>
      </h3>
      <p className="mt-2 flex flex-wrap items-center gap-2 text-[11px] leading-[17px] text-ink-6">
        {meta.map((m, i) => (
          <Fragment key={`${m}-${i}`}>
            {i > 0 && <span className="text-[#d5d5d5]">·</span>}
            <span>{m}</span>
          </Fragment>
        ))}
      </p>
      {drug.minPrescriptionAmount != null && (
        <p className="mt-1.5 text-[11px] leading-[17px] text-warning">
          최소 처방 금액 {formatWon(drug.minPrescriptionAmount)}
        </p>
      )}

      <div className="mt-3.5 flex rounded-lg bg-subtle px-4 py-3">
        <Stat label="약가" value={formatWon(drug.price)} tone="text-ink" />
        {rates.map((r) => (
          <Fragment key={r.label}>
            <span className="h-7 w-px shrink-0 bg-line-soft" />
            <Stat
              label={r.label}
              value={formatFeeRate(r.value)}
              tone={r.value == null ? 'text-[#bbb]' : 'text-primary'}
              className="pl-3"
            />
          </Fragment>
        ))}
      </div>

      <div className="mt-2.5 flex items-center">
        <span className="text-[10px] leading-[15px] text-ink-8">
          의약품 정보 기준 {formatYm(drug.referenceYearMonth)}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="ml-auto text-xs leading-[18px] font-medium text-primary hover:underline"
        >
          상세 보기 ›
        </button>
      </div>
    </article>
  );
};

const Stat = ({
  label,
  value,
  tone,
  className,
}: {
  label: string;
  value: string;
  tone: string;
  className?: string;
}) => (
  <div className={cn('flex min-w-0 flex-1 flex-col gap-1', className)}>
    <span className="truncate text-[10px] leading-[15px] text-ink-7">
      {label}
    </span>
    <span className={cn('text-[15px] leading-[23px] font-bold', tone)}>
      {value}
    </span>
  </div>
);
